// packages/i18n/src/client/contexts/I18nContext.tsx
'use client';

import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { NextIntlClientProvider } from 'next-intl';
import { fetchWithTimeout } from '@tourtask/utils/shared/fetchWithTimeout';
import type { UrlMap, NavItem, LeafNavItem } from '@tourtask/types/navigation';
import { defaultTimeZone } from '../../config';
import type { AppLocale } from '@tourtask/types/i18n';
import type { BackofficeUrlTarget } from '@tourtask/i18n/backofficeUrls';
import type { CommonKey } from '@tourtask/i18n/keys';
import { findNavItemByUrlCode } from '@tourtask/i18n/shared/utils/findNavItemByUrlCode';
import { fillParams } from '@tourtask/i18n/shared/utils/fillParams';
import { translatePathname as translatePathnameShared } from '@tourtask/i18n/shared/utils/translatePathname';
import { urlCodeOfPathname } from '@tourtask/i18n/shared/utils/urlCodeOfPathname';

/**
 * The message-map shape NextIntlClientProvider accepts. Derived from the
 * provider's own props so it tracks next-intl's type across upgrades without
 * importing a server-only symbol into this client module.
 */
type IntlMessages = NonNullable<ComponentProps<typeof NextIntlClientProvider>['messages']>;
type IntlProviderMessages = ComponentProps<typeof NextIntlClientProvider>['messages'];

/** The (locale, messages) pair handed to next-intl. */
type IntlState = { locale: AppLocale; messages: IntlProviderMessages };

/**
 * What this tab's provider last held. A root layout may send the messages and the URL map with a page
 * load only, never with a client-side request for a page's data (the backoffice's does: they are about
 * 830 KB of every refresh, docs/scaling.md §4), and the provider keeps what it was given. A provider
 * mounted again by such a later render (an error boundary's retry, a wrapper that remounts its children)
 * takes them from here. Browser only: on the server one module instance serves every request, so it is
 * neither read nor written there.
 */
let heldByThisTab: { intl: IntlState; urlMap: UrlMap } | null = null;

const LOCALE_FETCH_TIMEOUT_MS = 10_000;
const LOCALE_FETCH_MAX_ATTEMPTS = 3;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Loads a locale namespace JSON for a *live* language switch. The initial
 * locale's messages are server-rendered and passed in as `initialMessages`, so
 * this never runs on first paint — it only fires when the user actively changes
 * language. Resilient by design (bounded timeout + a few retries) because that
 * switch may happen on a flaky link, and it must never hang the app.
 */
async function fetchLocaleMessages(locale: AppLocale, namespace: 'common' | 'public'): Promise<IntlMessages> {
  let lastError: unknown;
  for (let attempt = 0; attempt < LOCALE_FETCH_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) await delay(Math.min(500 * 2 ** (attempt - 1), 2000));
    try {
      const res = await fetchWithTimeout(`/locales/${locale}/${namespace}.json`, {
        timeoutMs: LOCALE_FETCH_TIMEOUT_MS,
      });
      if (!res.ok) {
        lastError = new Error(`Failed to load /locales/${locale}/${namespace}.json (HTTP ${res.status})`);
        continue;
      }
      return (await res.json()) as IntlMessages;
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error(`Failed to load messages for "${locale}"`);
}
type I18nContextType = {
  locale: AppLocale;
  setLocale: (locale: AppLocale) => void;
  getUrl: (params: GetUrlParams) => string;
  /**
   * Like {@link getUrl} but returns `null` instead of throwing when the code is
   * absent from the active app's URL map. For OPTIONAL links — e.g. shared
   * components rendered outside the backoffice (the public ops-calendar demo)
   * whose backoffice-page menu entries should simply not render where the target
   * page doesn't exist.
   */
  tryGetUrl: (params: GetUrlParams) => string | null;
  /** The matched nav item's `pageTitleCode` — a catalog key, so the title is `t(code)`, checked. */
  getTitleCodeFromPath: (pathname: string, navigationConfig: ReadonlyArray<TitledNavItem>) => CommonKey | null;
  translatePathname: (pathname: string, targetLocale: AppLocale) => string;
  /**
   * The code of the mounted map whose pattern matches `pathname`. Deliberately `string`: the
   * public app mounts this provider with its own map, and proving a backoffice code here
   * would ship the whole backoffice catalog in every public page's bundle — and so would a
   * client caller narrowing it with the runtime `isParamFreeBackofficeUrlCode` guard. To
   * re-link the current page in another locale, use `translatePathname` above, which works
   * from the mounted map and carries the live path's `:param` values over.
   */
  getUrlCodeFromPath: (pathname: string) => string | null;
  getNavItemFromPath: (params: { pathname: string; navigationConfig: ReadonlyArray<NavItem> }) => NavItem | null;
};

/**
 * Backoffice codes whichever map the provider holds: every `getUrl`/`tryGetUrl` caller —
 * backoffice pages and the shared `@tourtask/ui` cells — links a backoffice page, and in the
 * public app (whose map has none of them) `tryGetUrl` returns null. A typo'd code, a
 * misspelled or missing replacement, and replacements on a param-free code are `tsc`
 * errors, as they already were on the server `getUrlsOf` path.
 */
type GetUrlParams = BackofficeUrlTarget & { locale?: AppLocale };

/** A nav tree whose labels and page titles are `common` catalog keys (the backoffice's). */
type TitledNavItem = NavItem<string, CommonKey>;

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n must be used within I18nProvider');
  return context;
}

type I18nProviderProps = {
  initialLocale: AppLocale;
  /**
   * Messages NESTED under their namespace — `{ common: { Save: 'Guardar' } }`, exactly what
   * `getMessages()` returns now that the request config uses `loadMessages`. Passing a flat
   * map compiles (next-intl's `messages` prop is deliberately loose) and then resolves
   * nothing at runtime, so nest at the call site: `{{ public: subset }}`.
   *
   * Read when the provider mounts and kept from then on (a live language switch replaces
   * them), so a root layout may leave them out of a later render: `undefined` = keep.
   */
  initialMessages?: IntlProviderMessages;
  /** Namespace to fetch if this provider owns a live client-side locale switch.
   *  Backoffice/admin keep the default `common`; apps that opt into a no-message
   *  root provider pass `initialMessages={null}` and normally navigate instead
   *  of live-switching messages. */
  messageNamespace?: 'common' | 'public';
  /** Every URL code's pattern per locale. Read at mount and kept, like `initialMessages`
   *  (it changes only with a deploy, which reloads a stale tab anyway). */
  urlMap?: UrlMap;
  children: ReactNode;
};

/**
 * On the server, a render without the messages or the URL map is Next's HTML answer to a script's fetch:
 * a root layout leaves both out of every `Sec-Fetch-Dest: empty` request (isPageLoadFromHeaders), which is
 * mostly the router's own data fetches, but a `fetch()` of a page's address, or of an unknown one (it
 * renders the not-found page, as the browser's own `/.well-known/…` checks do), gets the page as HTML. No
 * browser shows that document, so it renders nothing and keeps its status, instead of a 500 for a request
 * that did nothing wrong. A page load that lacks them is still a bug, and still throws, in the browser.
 */
export function I18nProvider(props: I18nProviderProps) {
  if (typeof window === 'undefined' && (props.initialMessages === undefined || props.urlMap === undefined)) return null;
  return <MountedI18nProvider {...props} />;
}

function MountedI18nProvider({
  initialLocale,
  initialMessages,
  messageNamespace = 'common',
  urlMap: urlMapProp,
  children,
}: I18nProviderProps) {
  // `intl` is the (locale, messages) pair currently handed to next-intl. It's
  // seeded from the server-rendered messages, so first paint has translations
  // with no extra round trip and no blank-screen gate. `locale` below can move
  // ahead of `intl.locale` during a live language switch (so URL helpers use
  // the target immediately); `intl` only advances once the new locale's
  // messages have loaded, so next-intl never renders a locale/messages mismatch.
  // A mount without messages (a render that left them out, see heldByThisTab)
  // resumes what the tab held; with nothing held it is a page load that forgot
  // them, which would paint raw keys, so it fails loudly instead.
  const [intl, setIntl] = useState<IntlState>(() => {
    if (initialMessages !== undefined) return { locale: initialLocale, messages: initialMessages };
    const held = typeof window === 'undefined' ? null : heldByThisTab;
    if (!held) throw new Error('I18nProvider mounted without messages: a page load must pass initialMessages');
    return held.intl;
  });
  const [urlMap] = useState<UrlMap>(() => {
    const map = urlMapProp ?? (typeof window === 'undefined' ? null : heldByThisTab?.urlMap);
    if (!map) throw new Error('I18nProvider mounted without a URL map: a page load must pass urlMap');
    return map;
  });
  const [locale, setLocale] = useState<AppLocale>(intl.locale);

  useEffect(() => {
    heldByThisTab = { intl, urlMap };
  }, [intl, urlMap]);

  useEffect(() => {
    if (locale === intl.locale) return; // Initial locale (and no-op switches): nothing to fetch.

    let cancelled = false;
    const targetLocale = locale;

    if (initialMessages === null) {
      setIntl({ locale: targetLocale, messages: null });
      return () => { cancelled = true; };
    }

    void (async () => {
      try {
        const flat = await fetchLocaleMessages(targetLocale, messageNamespace);
        if (cancelled) return;
        // `/locales/<locale>/<ns>.json` is a FLAT file, while `initialMessages` arrives
        // already nested from `getMessages()`. Nesting here is what keeps the two paths
        // producing the same shape — a live language switch that handed next-intl a flat
        // map would silently resolve nothing, because every lookup is `<ns>.<key>`.
        setIntl({ locale: targetLocale, messages: { [messageNamespace]: flat } });
      } catch (err) {
        if (cancelled) return;
        // Keep showing the current language rather than blanking the app — the
        // switch just didn't take. Surfaced for diagnosis (no silent failure).
        console.error(`[I18nProvider] failed to load messages for "${targetLocale}"`, err);
      }
    })();

    return () => { cancelled = true; };
  }, [locale, intl.locale, initialMessages, messageNamespace]);

  const getUrl = ({
    code,
    replacements,
    locale: overrideLocale
  }: GetUrlParams): string => {
    const localeToUse = overrideLocale ?? locale;
    const pattern = urlMap[localeToUse]?.[code];
    if (!pattern) {
      throw new Error(`No URL found for code "${code}" in language "${localeToUse}".`);
    }
    return fillParams(pattern, replacements);
  };

  const tryGetUrl = ({ code, replacements, locale: overrideLocale }: GetUrlParams): string | null => {
    const localeToUse = overrideLocale ?? locale;
    const pattern = urlMap[localeToUse]?.[code];
    if (!pattern) return null;
    return fillParams(pattern, replacements);
  };

  const getNavItemFromPath = ({ pathname, navigationConfig }: { pathname: string; navigationConfig: ReadonlyArray<NavItem> }): NavItem | null => {
    const urlCode = getUrlCodeFromPath(pathname);
    if (!urlCode) return null;
    return findNavItemByUrlCode(navigationConfig, urlCode) ?? null;
  };

  // The best pattern match (supports :id etc.), shared with the server (urlCodeOfPathname). An
  // unmatched path is null, so callers fall back instead of an unhandled throw blanking the client tree.
  const getUrlCodeFromPath = (pathname: string): string | null => urlCodeOfPathname(urlMap, pathname);

// Delegates to the shared isomorphic implementation so the client-side
// language switch and the backoffice server layout's first-load locale
// redirect translate paths identically by construction.
const translatePathname = (pathname: string, targetLocale: AppLocale): string =>
  translatePathnameShared({ pathname, targetLocale, urlMap });


const stripParamSuffix = (code: string) => code.replace(/\/:[A-Za-z0-9_]+$/, '');

const getTitleCodeFromPath = (pathname: string, navigationConfig: ReadonlyArray<TitledNavItem>): CommonKey | null => {
  const rawCode = getUrlCodeFromPath(pathname);
  if (!rawCode) return null;
  const urlCode = stripParamSuffix(rawCode); // <-- normalize to base code

  function isLeafNavItem(item: TitledNavItem): item is LeafNavItem<string, CommonKey> {
    return 'pageTitleCode' in item;
  }

  const findTitleCode = (items: ReadonlyArray<TitledNavItem>): CommonKey | null => {
    for (const item of items) {
      if (item.urlCode === urlCode && isLeafNavItem(item)) return item.pageTitleCode;
      if (item.children) {
        const child = findTitleCode(item.children);
        if (child) return child;
      }
    }
    return null;
  };

  // Returns null when the resolved code has no nav entry — callers fall back to
  // a default title rather than crash on a path the nav config doesn't list.
  return findTitleCode(navigationConfig);
};

  return (
    <I18nContext.Provider value={{ locale, setLocale, getUrl, tryGetUrl, getTitleCodeFromPath, translatePathname, getUrlCodeFromPath, getNavItemFromPath }}>
      <NextIntlClientProvider
        locale={intl.locale}
        timeZone={defaultTimeZone}
        messages={intl.messages}
        onError={(error) => {
          // Never let a missing message / formatting error escalate into an
          // unhandled throw that blanks the app — log it for diagnosis instead.
          console.error('[next-intl]', error);
        }}
        getMessageFallback={({ key }) => key}
      >
        {children}
      </NextIntlClientProvider>
    </I18nContext.Provider>
  );
}
